// LocationRow.jsx
import React, { useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Chip,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Button,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import BuildingRow from "./BuildingRow";
import {
  textFieldSx,
  formatCoords,
  formatArea,
  formatAreaInput,
  coordsToJSON,
  handleCoordinateChange,
  handleCoordinateBlur,
} from "./utils/tableHelpers";
import { useExternalToggle } from "../hooks/useToggle";

export default function LocationRow({
  location,
  buildingsByLocation,
  getLocationTypeName,
  locationTypes = [],
  departmentTypes = [],
  roomTypes = [],
  floorTypes = [],
  onSaveLocation,
  onSaveBuilding,
  departmentsByBuilding,
  roomsByDepartment,
  onSaveDepartment,
  onSaveRoom,
  isOpen,
  onToggle,
}) {
  const { isOpen: toggleIsOpen, toggle } = useExternalToggle(isOpen, onToggle);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(location);
  const [tempBuildings, setTempBuildings] = useState([]);
  const buildings = buildingsByLocation[location.pk_id] || [];

  const handleEdit = () => {
    setEditData({ ...location });
    setEditing(true);
  };

  const handleCancel = () => {
    setEditData(location);
    setEditing(false);
  };

  const handleSave = () => {
    const payload = {
      ...editData,
      pk_id: location.pk_id,
      location_coordinates: coordsToJSON(editData.location_coordinates),
    };
    onSaveLocation?.(payload);
    setEditing(false);
  };

  const handleChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateBuilding = () => {
    const newBuilding = {
      pk_id: `temp_${Date.now()}`, // Temporary ID
      building_name: "",
      building_address: "",
      building_address_2: "",
      building_postcode: "",
      building_coordinates: "{}",
      building_area: null,
      number_of_floors: null,
      building_access_details: "",
      location_pk_id: location.pk_id,
      isNew: true,
      isEditing: true,
    };
    setTempBuildings((prev) => [...prev, newBuilding]);
  };

  const handleSaveTempBuilding = async (buildingData) => {
    try {
      // Remove the temporary fields before saving
      const { isNew, isEditing, ...cleanBuildingData } = buildingData;
      delete cleanBuildingData.pk_id; // Remove temp ID for new buildings

      await onSaveBuilding?.(cleanBuildingData);
      // Remove from temp buildings after successful save
      setTempBuildings((prev) =>
        prev.filter((b) => b.pk_id !== buildingData.pk_id),
      );
    } catch (error) {
      console.error("Failed to save building:", error);
      throw error;
    }
  };

  const handleCancelTempBuilding = (tempId) => {
    setTempBuildings((prev) => prev.filter((b) => b.pk_id !== tempId));
  };

  return (
    <>
      <TableRow
        hover
        onClick={toggle}
        sx={{
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <TableCell padding="checkbox">
          <IconButton size="small">
            {toggleIsOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title={location.location_name || ""}>
              <span
                style={{
                  display: "inline-block",
                  maxWidth: 360,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {location.location_name}
              </span>
            </Tooltip>
            {buildings.length > 0 && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}
              >
                <BusinessIcon sx={{ fontSize: 16, color: "#666" }} />
                <Typography variant="caption" sx={{ color: "#666" }}>
                  {buildings.length}
                </Typography>
              </Box>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            label={
              getLocationTypeName
                ? getLocationTypeName(location.location_type_id)
                : (location.location_type_id ?? "-")
            }
            size="small"
          />
        </TableCell>
        <TableCell>
          <Tooltip title={location.location_address || ""}>
            <span
              style={{
                display: "inline-block",
                maxWidth: 400,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {location.location_address || "-"}
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={toggleIsOpen} timeout="auto" unmountOnExit>
            <Box sx={{ ml: 4, my: 2, marginLeft: 0 }}>
              <Card
                variant="outlined"
                sx={{ mb: 2, backgroundColor: "#f9fafb" }}
              >
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 10 }}>
                      <Typography variant="subtitle2" color="primary">
                        Location Details
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      {!editing ? (
                        <IconButton size="small" onClick={handleEdit}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <>
                          <IconButton
                            size="small"
                            onClick={handleSave}
                            color="success"
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleCancel}
                            color="error"
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Grid>

                    {!editing ? (
                      <Grid
                        container
                        spacing={2}
                        sx={{ wordBreak: "break-word" }}
                      >
                        <Grid size={{ xs: 3, md: 4 }}>
                          <Typography variant="caption" color="text.secondary">
                            Name:
                          </Typography>
                          <Typography variant="body2">
                            {location.location_name}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 3, md: 3 }}>
                          <Typography variant="caption" color="text.secondary">
                            Location Type:
                          </Typography>
                          <Typography variant="body2">
                            {getLocationTypeName
                              ? getLocationTypeName(location.location_type_id)
                              : (location.location_type_id ?? "-")}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 3, md: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Total Area:
                          </Typography>
                          <Typography variant="body2">
                            {formatArea(location.location_area)}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 3, md: 3 }}>
                          <Typography variant="caption" color="text.secondary">
                            Number of Buildings:
                          </Typography>
                          <Typography variant="body2">
                            {location.number_of_buildings ??
                              buildings?.length ??
                              0}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 12 }}>
                          <Typography variant="caption" color="text.secondary">
                            Address:
                          </Typography>
                          <Typography variant="body2">
                            {location.location_address || "-"}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <Typography variant="caption" color="text.secondary">
                            Address 2:
                          </Typography>
                          <Typography variant="body2">
                            {location.location_address_2 || "-"}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 4, md: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            City:
                          </Typography>
                          <Typography variant="body2">
                            {location.location_city || "-"}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 4, md: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            County:
                          </Typography>
                          <Typography variant="body2">
                            {location.location_county || "-"}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 4, md: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Postcode:
                          </Typography>
                          <Typography variant="body2">
                            {location.location_postcode || "-"}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 4, md: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Country:
                          </Typography>
                          <Typography variant="body2">
                            {location.location_country || "-"}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 4, md: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Coordinates:
                          </Typography>
                          <Typography variant="body2">
                            {formatCoords(location.location_coordinates)}
                          </Typography>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid
                        container
                        spacing={2}
                        sx={{ wordBreak: "break-word" }}
                      >
                        <Grid size={{ xs: 4, md: 4 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Name:
                          </Typography>
                          <TextField
                            size="small"
                            value={editData.location_name || ""}
                            onChange={(e) =>
                              handleChange("location_name", e.target.value)
                            }
                            fullWidth
                            sx={textFieldSx}
                          />
                        </Grid>
                        <Grid size={{ xs: 4, md: 3 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Location Type:
                          </Typography>
                          <TextField
                            select
                            size="small"
                            value={editData.location_type_id || ""}
                            onChange={(e) =>
                              handleChange("location_type_id", e.target.value)
                            }
                            fullWidth
                            sx={textFieldSx}
                          >
                            {locationTypes.map((t) => (
                              <MenuItem key={t.pk_id} value={t.pk_id}>
                                {t.type_name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid size={{ xs: 4, md: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Total Area (m²):
                          </Typography>
                          <TextField
                            disabled
                            size="small"
                            type="number"
                            value={formatAreaInput(
                              editData.location_area ?? location.location_area,
                            )}
                            onChange={(e) =>
                              handleChange(
                                "location_area",
                                parseInt(e.target.value, 10),
                              )
                            }
                            fullWidth
                            sx={textFieldSx}
                          />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Address:
                          </Typography>
                          <TextField
                            size="small"
                            value={editData.location_address || ""}
                            onChange={(e) =>
                              handleChange("location_address", e.target.value)
                            }
                            multiline
                            rows={2}
                            fullWidth
                            sx={textFieldSx}
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Address 2:
                          </Typography>
                          <TextField
                            size="small"
                            value={editData.location_address_2 || ""}
                            onChange={(e) =>
                              handleChange("location_address_2", e.target.value)
                            }
                            fullWidth
                            sx={textFieldSx}
                          />
                        </Grid>
                        <Grid size={{ xs: 4, md: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            City:
                          </Typography>
                          <TextField
                            size="small"
                            value={editData.location_city || ""}
                            onChange={(e) =>
                              handleChange("location_city", e.target.value)
                            }
                            fullWidth
                            sx={textFieldSx}
                          />
                        </Grid>
                        <Grid size={{ xs: 4, md: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            County:
                          </Typography>
                          <TextField
                            size="small"
                            value={editData.location_county || ""}
                            onChange={(e) =>
                              handleChange("location_county", e.target.value)
                            }
                            fullWidth
                            sx={textFieldSx}
                          />
                        </Grid>
                        <Grid size={{ xs: 4, md: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Postcode:
                          </Typography>
                          <TextField
                            size="small"
                            value={editData.location_postcode || ""}
                            onChange={(e) =>
                              handleChange("location_postcode", e.target.value)
                            }
                            fullWidth
                            sx={textFieldSx}
                          />
                        </Grid>
                        <Grid size={{ xs: 4, md: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Country:
                          </Typography>
                          <TextField
                            size="small"
                            value={editData.location_country || ""}
                            onChange={(e) =>
                              handleChange("location_country", e.target.value)
                            }
                            fullWidth
                            sx={textFieldSx}
                          />
                        </Grid>

                        <Grid size={{ xs: 4, md: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Coordinates (lat, lon):
                          </Typography>
                          <TextField
                            size="small"
                            placeholder="lat, lon"
                            defaultValue={formatCoords(
                              location.location_coordinates,
                            )}
                            onChange={(e) => {
                              handleCoordinateChange(e.target.value, (value) =>
                                handleChange("location_coordinates", value),
                              );
                            }}
                            onBlur={(e) => {
                              handleCoordinateBlur(e.target.value, (value) =>
                                handleChange("location_coordinates", value),
                              );
                            }}
                            fullWidth
                            sx={textFieldSx}
                          />
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Number of Buildings:
                          </Typography>
                          <TextField
                            size="small"
                            disabled
                            type="number"
                            value={
                              editData.number_of_buildings ||
                              location.number_of_buildings ||
                              buildings?.length ||
                              0
                            }
                            onChange={(e) =>
                              handleChange(
                                "number_of_buildings",
                                parseInt(e.target.value, 10),
                              )
                            }
                            fullWidth
                            sx={textFieldSx}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Buildings
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleCreateBuilding}
                  variant="outlined"
                  sx={{
                    minWidth: "auto",
                    fontSize: "0.75rem",
                    py: 0.25,
                    px: 1,
                  }}
                >
                  Add Building
                </Button>
              </Box>
              {buildings.length === 0 && tempBuildings.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No buildings
                </Typography>
              ) : (
                <Table size="small" aria-label="buildings table">
                  <TableBody>
                    {buildings.map((b) => (
                      <BuildingRow
                        key={b.pk_id}
                        building={b}
                        location={location}
                        departmentsByBuilding={departmentsByBuilding}
                        roomsByDepartment={roomsByDepartment}
                        departmentTypes={departmentTypes}
                        roomTypes={roomTypes}
                        floorTypes={floorTypes}
                        onSaveBuilding={onSaveBuilding}
                        onSaveDepartment={onSaveDepartment}
                        onSaveRoom={onSaveRoom}
                      />
                    ))}
                    {tempBuildings.map((b) => (
                      <BuildingRow
                        key={b.pk_id}
                        building={b}
                        location={location}
                        departmentsByBuilding={{}}
                        roomsByDepartment={{}}
                        departmentTypes={departmentTypes}
                        roomTypes={roomTypes}
                        floorTypes={floorTypes}
                        onSaveBuilding={handleSaveTempBuilding}
                        onSaveDepartment={onSaveDepartment}
                        onSaveRoom={onSaveRoom}
                        onCancel={() => handleCancelTempBuilding(b.pk_id)}
                        forceEdit={true}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
